export type LayersState = {
  highlightHandle: __esri.Handle | null;
  highlightLayerView: __esri.GraphicsLayerView | __esri.FeatureLayerView | null;
  highlightLayer: __esri.GraphicsLayer | null;
  bufferLayer: __esri.GraphicsLayer | null;
  markupLayer: __esri.GraphicsLayer | null;
  primaryLayer: __esri.FeatureLayer | null;
  primaryQueryLayer: __esri.FeatureLayer | null;
  primaryLayerView: __esri.FeatureLayerView | null;
  layerVisibility: Record<string, boolean>;
  layerOpacity: Record<string, number>;
  alternateLayer: __esri.FeatureLayer | __esri.MapImageLayer | null;
  alternateQueryLayer: __esri.FeatureLayer | null;
};

export type LayersActions = {
  setBufferLayer: (layer: __esri.GraphicsLayer) => void;
  setPrimaryLayer: (layer: __esri.FeatureLayer) => void;
  setPrimaryLayerView: (layerView: __esri.FeatureLayerView) => void;
  setPrimaryQueryLayer: (layer: __esri.FeatureLayer) => void;
  setBufferGraphic: (graphic: __esri.Graphic | null) => void;
  setMarkupLayer: (layer: __esri.GraphicsLayer) => void;
  setHighlightLayerView: (
    layerView: __esri.GraphicsLayerView | __esri.FeatureLayerView
  ) => void;
  setHighlightHandle: (handle: __esri.Handle | null) => void;
  setHighlightLayer: (layer: __esri.GraphicsLayer) => void;
  getLayerById: (layerId: string) => __esri.FeatureLayer | null;
  getLayerVisibilityById: (layerId: string) => boolean;
  setLayerVisibility: (layerId: string, visible: boolean) => void;
  toggleLayerVisibility: (layerId: string) => void;
  getLayerOpacityById: (layerId: string) => number;
  setLayerOpacity: (layerId: string, opacity: number) => void;
  getLayerTitleById: (layerId: string) => string;
  setAlternateLayer: (layer: __esri.FeatureLayer) => void;
  setAlternateQueryLayer: (layer: __esri.FeatureLayer) => void;
};

export const createLayersSlice = (set, get): LayersState & LayersActions => ({
  layerVisibility: {
    "parcels-image": true,
  },
  layerOpacity: {},
  highlightLayer: null,
  setHighlightLayer: (layer) => set({ highlightLayer: layer }),
  highlightHandle: null,
  setHighlightHandle: (handle) => {
    if (get().highlightHandle) {
      get().highlightHandle.remove();
    }
    set({ highlightHandle: handle });
  },
  highlightLayerView: null,
  setHighlightLayerView: (layerView) => set({ highlightLayerView: layerView }),
  bufferLayer: null,
  setBufferLayer: (layer) => set({ bufferLayer: layer }),
  markupLayer: null,
  setMarkupLayer: (layer) => set({ markupLayer: layer }),
  primaryLayer: null,
  getLayerTitleById: (layerId: string) => {
    const layer = get().getLayerById(layerId);
    return layer ? layer.title : "";
  },
  getLayerOpacityById: (layerId: string) => {
    return get().layerOpacity[layerId];
  },
  setLayerOpacity: (layerId: string, opacity: number) => {
    set({ layerOpacity: { ...get().layerOpacity, [layerId]: opacity } });
    const layer = get().getLayerById(layerId);
    if (!layer) {
      return;
    }
    layer.opacity = opacity;
  },
  getLayerById: (layerId: string) => {
    const map = get().map;
    if (!map) {
      return null;
    }
    return map.findLayerById(layerId);
  },
  getLayerVisibilityById: (layerId: string) => {
    return get().layerVisibility[layerId];
  },
  setLayerVisibility: (layerId: string, visible: boolean) => {
    set({
      layerVisibility: { ...get().layerVisibility, [layerId]: visible },
    });
    const layer = get().getLayerById(layerId);
    if (!layer) {
      return;
    }

    if (layer.type === "tile" && visible) {
      const layerConfig = get().config.secondaryLayers.find(
        (id) => id.id === layerId
      );

      if (layerConfig.goToMiddleScale) {
        get().mapView.scale =
          layer.tileInfo.scales[
            Math.floor(layer.tileInfo.scales.length / 2) + 1
          ];
      }
    }

    layer.visible = visible;
  },
  toggleLayerVisibility: async (layerId: string) => {
    const layer = get().getLayerById(layerId);
    const layerConfig = get().config.secondaryLayers.find(
      (id) => id.id === layerId
    );

    if (layerConfig) {
      const allowedWithLayers = layerConfig.allowedWithLayers || [];
      if (layer && layerConfig.zoomToLayer && !layer.visible) {
        if (layer.type === "tile") {
          // const _scale = layer.tileInfo.scales[2];
          await get().mapView.goTo({ target: layer.initialExtent });
          const _scale =
            layer.sublayers.items[layer.sublayers.items.length - 1].minScale;
          if (_scale && _scale < get().mapView.scale) {
            get().mapView.scale = _scale;
          }
        } else {
          get().mapView.goTo({ target: layer.fullExtent });
        }
        // if (layer.type === "")
      }

      const hasAsterisk = allowedWithLayers.some((l) => l === "*");
      if (!hasAsterisk) {
        get().map.layers.forEach((l) => {
          if (l.id === layerId) return;
          if (!allowedWithLayers || !allowedWithLayers.includes(l.id)) {
            const _layConfig = get().config.secondaryLayers.find(
              (secLay) => secLay.id === l.id
            );
            if (_layConfig && _layConfig.showInTOC) {
              get().setLayerVisibility(l.id, false);
            }
          }
        });
      }
    }
    if (!layer) {
      return;
    }
    get().setLayerVisibility(layerId, !layer.visible);
    if (layerId === "existingLandUse") {
      const parcelsLayer = get().getLayerById("parcels-image");
      if (parcelsLayer && parcelsLayer.visible) {
        const parcelOpacity = layer && layer.visible ? 0 : 1;
        parcelsLayer.opacity = parcelOpacity;
      }
    }
  },
  primaryQueryLayer: null,
  setPrimaryQueryLayer: (layer) => set({ primaryQueryLayer: layer }),
  setPrimaryLayer: (layer) => set({ primaryLayer: layer }),
  alternateLayer: null,
  alternateQueryLayer: null,
  setAlternateLayer: (layer) => set({ alternateLayer: layer }),
  setAlternateQueryLayer: (layer) => set({ alternateQueryLayer: layer }),
  primaryLayerView: null,
  setPrimaryLayerView: (layerView) => set({ primaryLayerView: layerView }),
  setBufferGraphic: (graphic) => set({ bufferGraphic: graphic }),
});
